import type { PriceBreakdown, Pricing } from '@/types/checkout';

const MULTIPLIER_DECIMAL_MAP = new Map([
  [1, 0],
  [100, 2],
  [1000, 3],
] as const);

const DEFAULT_DECIMAL_PLACES = 2;

const getDecimalPlaces = (multiplier?: number): number => {
  if (!multiplier) return DEFAULT_DECIMAL_PLACES;
  return MULTIPLIER_DECIMAL_MAP.get(multiplier as 1 | 100 | 1000) ?? DEFAULT_DECIMAL_PLACES;
};

export const getFormattedNumber = (number: number, pricing: Pricing): string => {
  const decimalNumber = pricing.isCents && pricing.multiplier ? number / pricing.multiplier : number;

  const decimalPlaces = getDecimalPlaces(pricing.multiplier);

  const fixedNumber = decimalPlaces === 0 ? Math.round(decimalNumber).toString() : decimalNumber.toFixed(decimalPlaces);

  const [integerPart, decimalPart] = fixedNumber.split('.');

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, pricing.milSeparator);

  const formattedDecimal = decimalPart || (decimalPlaces > 0 ? '0'.repeat(decimalPlaces) : '');

  return decimalPlaces === 0
    ? formattedInteger
    : `${formattedInteger}${pricing.fractionalSeparator}${formattedDecimal}`;
};

export const getFormattedCurrency = (currency: string): string => {
  return currency.toUpperCase();
};

export const getFormattedCountryCode = (countryCode: string): string => {
  return countryCode.toUpperCase();
};

export const calculateTotalPrice = (pricing: Pricing): string => {
  const formattedNumber = getFormattedNumber(pricing.priceBreakdown.priceTotal, pricing);
  return `${pricing.symbolPosition === 'Left' ? pricing.currencySymbol : ''}${formattedNumber}${pricing.symbolPosition === 'Right' ? pricing.currencySymbol : ''}`;
};

export const convertToDecimalAmount = (num: number, multiplier: number): string => {
  return multiplier === 1 ? num.toString() : (num / multiplier).toString();
};

export const getSubTotalPricing = (priceBreakdown: PriceBreakdown, pricing: Pricing): string => {
  return `${pricing.symbolPosition === 'Left' ? pricing.currencySymbol : ''}${getFormattedNumber(priceBreakdown.baseNet, pricing)}${pricing.symbolPosition === 'Right' ? pricing.currencySymbol : ''}`;
};

export const calculatePromoCode = (priceBreakdown: PriceBreakdown, pricing: Pricing): string => {
  return `${pricing.symbolPosition === 'Left' ? pricing.currencySymbol : ''}${getFormattedNumber(priceBreakdown.discount, pricing)}${pricing.symbolPosition === 'Right' ? pricing.currencySymbol : ''}`;
};

export const calculateTax = (priceBreakdown: PriceBreakdown, pricing: Pricing): string => {
  if (!priceBreakdown.isTaxValid) return '–';
  return `${pricing.symbolPosition === 'Left' ? pricing.currencySymbol : ''}${getFormattedNumber(priceBreakdown.tax, pricing)}${pricing.symbolPosition === 'Right' ? pricing.currencySymbol : ''}`;
};

/** Total = final price (after discount, with tax). Matches v2: use priceTotal only. */
export const getTotalPricing = (pricing: Pricing): string => {
  return `${pricing.symbolPosition === 'Left' ? pricing.currencySymbol : ''}${getFormattedNumber(pricing.priceBreakdown.priceTotal, pricing)}${pricing.symbolPosition === 'Right' ? pricing.currencySymbol : ''}`;
};

export const calculateTaxPrecent = (priceBreakdown: PriceBreakdown): string => {
  return `${priceBreakdown.taxRatePoints / 100}%`;
};

export const addCommasToNumber = (number: number): string => {
  return number.toLocaleString();
};

/*
 * Get payment request amount for Adyen/Google Pay transactionInfo.
 */
export const getPaymentRequestAmount = (number?: number, pricing?: Pricing): string => {
  if (number == null || Number.isNaN(number)) return '0.00';
  const value = pricing?.isCents && pricing?.multiplier ? number / pricing.multiplier : number;
  const decimalPlaces = getDecimalPlaces(pricing?.multiplier);
  return value.toFixed(decimalPlaces);
};
